import { useEffect, useState } from "react";
import ProfitIcon from "./dashboard/icons/ProfitIcon";
import { generateTotalRevenue } from "./TotalRevenue";
import { useSelector } from "react-redux";
import ReactCardFlip from "react-card-flip";
import {
  Typography,
  Box,
  Table,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  Paper,
  TableContainer,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ThreeSixtyIcon from "@mui/icons-material/ThreeSixty";
import { useTranslation } from "react-i18next";

const generateProfitPaylod = (obj, facility, currency = " ") => {
  let arr = [];
  let count = 0;
  let total = 0;
  arr[count++] = {
    type: "Revenue",
    units:
      "AC:" + obj.units_consumed_ac + " || " + "DC:" + obj.units_consumed_dc,
    price:
      "AC:" +
      facility.price_per_unit_ac +
      " || " +
      "DC:" +
      facility.price_per_unit_dc,
    amount: obj.revenue?.toFixed(2),
  };
  total += obj.amount;
  if (facility?.price_per_unit_electricity) {
    arr[count++] = {
      type: "Electricity",
      price: currency + facility?.price_per_unit_electricity,
      units: obj.units_consumed?.toFixed(2),
      amount: obj.electricity_total?.toFixed(2),
    };
    total += obj.electricity_total;
  }
  if (facility?.is_profit_sharing) {
    arr[count++] = {
      type: "Station Profit",
      price:
        (facility?.profit_sharing_type === "Fixed" ? currency : "") +
        facility?.profit_sharing_amount.toFixed(2) +
        (facility?.profit_sharing_type !== "Fixed" ? "%" : "") +
        (facility?.profit_sharing_type === "Fixed" ? "(F)" : "(P)"),
      units: obj.units_consumed?.toFixed(2),
      amount:
        facility?.profit_sharing_type === "Fixed"
          ? (facility?.profit_sharing_amount * obj.units_consumed)?.toFixed(2)
          : (
              (obj?.withoutCompanyProfit * facility?.profit_sharing_amount) /
              100
            )?.toFixed(2),
    };
    total += arr[count]?.amount;
  }
  arr[count] = {
    type: "Total",
    units: "-",
    price: "-",
    amount: obj.profit?.toFixed(2),
  };
  return arr;
};

const TotalProfit = ({ stats }) => {
  const [profit, setProfit] = useState(0.0);
  const { currency } = useSelector((state) => state.user);
  const stationlist = useSelector((state) => state.stationList.data.data);
  const { selectedFacility } = useSelector((state) => state.stationList);
  const [rows, setRows] = useState(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(false);
  // const ref = useRef(data?.view_facility_revenue);
  // ref.current = data?.view_facility_revenue;
  const { t } = useTranslation();

  useEffect(() => {
    setLoading(true);
    if (stats) {
      setProfit(
        selectedFacility?.name !== "All"
          ? Number(stats.profit.toFixed(2)).toLocaleString()
          : "N/A"
      );

      if (selectedFacility?.is_fleet) {
        setRows(generateProfitPaylod(stats, selectedFacility, currency));
      }
    }
    setLoading(false);
  }, [stats, selectedFacility, stationlist, currency]);

  const handleFlip = () => {
    setIsFlipped((prev) => !prev);
  };

  return (
    <Box>
      <ReactCardFlip
        isFlipped={isFlipped}
        flipDirection="horizontal"
        flipSpeedFrontToBack="0.6"
        flipSpeedBackToFront="0.6"
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          bgcolor={"white"}
          justifyContent={"space-between"}
          borderRadius={"16px"}
          padding="24px"
          height="150px"
          key="front"
        >
          <Typography>
            <Typography
              variant="subtitle"
              fontSize="12px"
              fontWeight="600"
              style={{ textWrap: "nowrap" }}
            >
              {t("terraProfit")}
              <Typography
                variant="subtitle"
                fontSize="9px"
                color="grey"
                pl="3px"
              >
                (incl. of all tax)
              </Typography>
              {loading ? (
                <span style={{ color: "#467CEB" }}> {t("loading")}...</span>
              ) : (
                ""
              )}
            </Typography>
            <Typography
              variant="h5"
              fontSize="24px"
              fontWeight="700"
              sx={{ marginTop: "10px" }}
              style={{ textWrap: "nowrap" }}
            >
              {selectedFacility?.name !== "All"
                ? `${currency} ${Number(profit ?? "0") < 0 ? 0 : profit}`
                : profit}
            </Typography>
          </Typography>
          <ProfitIcon />
          {selectedFacility?.is_fleet && (
            <Tooltip
              title="Info"
              sx={{
                position: "absolute",
                top: "-3px",
                right: "0px",
                color: "grey",
              }}
            >
              <IconButton>
                <Box onClick={handleFlip}>
                  <InfoOutlinedIcon />
                </Box>
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {selectedFacility?.is_fleet && (
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            borderRadius={"16px"}
            height="150px"
            key="back"
            overflow="hidden"
          >
            <TableContainer
              component={Paper}
              sx={{
                height: "100%",
                width: "100%",
                overflow: "hidden",
                padding: "5px 5px 0",
                border: "none",
                backgroundColor: "grey",
                color: "white",
              }}
            >
              <Typography
                variant="h5"
                fontSize="12px"
                fontWeight="600"
                padding="5px"
                textAlign="center"
              >
                {t("terraProfit")}
              </Typography>
              <Table aria-label="simple table" sx={{ margin: "10px 0 0" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      align="center"
                      sx={{ padding: "2px", fontSize: "11px", color: "white" }}
                    >
                      {t("type")}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "2px", fontSize: "11px", color: "white" }}
                    >
                      {t("units")}&nbsp;(kWh)
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "2px", fontSize: "11px", color: "white" }}
                    >
                      {currency || " "}/kWh
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "2px", fontSize: "11px", color: "white" }}
                    >
                      {t("amount")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows &&
                    rows.map((row) => (
                      <TableRow
                        key={row.type}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          sx={{
                            padding: "2px",
                            fontSize: "10px",
                            color: "white",
                          }}
                        >
                          {row.type}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            padding: "2px",
                            fontSize: "8px",
                            color: "white",
                          }}
                        >
                          {row.units}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            padding: "2px",
                            fontSize: "8px",
                            color: "white",
                          }}
                        >
                          {row.price}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            padding: "2px",
                            fontSize: "8px",
                            color: "white",
                          }}
                        >
                          {currency || " "}
                          {row.amount}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Tooltip
              title="Rotate"
              sx={{
                position: "absolute",
                top: "-3px",
                right: "0px",
                color: "white",
              }}
            >
              <IconButton>
                <Box onClick={handleFlip}>
                  <ThreeSixtyIcon />
                </Box>
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </ReactCardFlip>
    </Box>
  );
};
export default TotalProfit;
